"use client";

import * as React from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  createDestination,
  deleteDestination,
  getDestinations,
  updateDestination,
  type DestinationMode,
  type WebhookDestination,
  type WebhookDestinationPayload,
} from "./actions";
import { Pencil, Plus, Trash2 } from "lucide-react";

const emptyForm: WebhookDestinationPayload = {
  name: "",
  url: "",
  token: "",
  header_key: "",
  header_value: "",
  mode: "production",
  is_active: true,
};

// Auth mode untuk keperluan UI form saja
type AuthMode = "none" | "bearer" | "custom";

function detectAuthMode(form: WebhookDestinationPayload): AuthMode {
  if (form.header_key) return "custom";
  if (form.token) return "bearer";
  return "none";
}

export function DestinationsTable() {
  const [data, setData] = React.useState<WebhookDestination[]>([]);
  const [loading, setLoading] = React.useState(true);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editTarget, setEditTarget] = React.useState<WebhookDestination | null>(null);
  const [form, setForm] = React.useState<WebhookDestinationPayload>(emptyForm);
  const [authMode, setAuthMode] = React.useState<AuthMode>("none");
  const [saving, setSaving] = React.useState(false);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    const result = await getDestinations();
    setData(result);
    setLoading(false);
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setEditTarget(null);
    setForm(emptyForm);
    setAuthMode("none");
    setDialogOpen(true);
  };

  const openEdit = (dest: WebhookDestination) => {
    setEditTarget(dest);
    const f: WebhookDestinationPayload = {
      name: dest.Name,
      url: dest.URL,
      token: dest.Token,
      header_key: dest.HeaderKey,
      header_value: dest.HeaderValue,
      mode: dest.Mode,
      is_active: dest.IsActive,
    };
    setForm(f);
    setAuthMode(detectAuthMode(f));
    setDialogOpen(true);
  };

  // Saat auth mode berubah, reset field yang tidak relevan
  const handleAuthModeChange = (v: AuthMode) => {
    setAuthMode(v);
    if (v === "none") {
      setForm((f) => ({ ...f, token: "", header_key: "", header_value: "" }));
    } else if (v === "bearer") {
      setForm((f) => ({ ...f, header_key: "", header_value: "" }));
    } else if (v === "custom") {
      setForm((f) => ({ ...f, token: "" }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    if (editTarget) {
      const updated = await updateDestination(editTarget.ID, form);
      if (updated) {
        setData((prev) =>
          prev.map((d) => (d.ID === updated.ID ? updated : d))
        );
      }
    } else {
      const created = await createDestination(form);
      if (created) {
        setData((prev) => [created, ...prev]);
      }
    }
    setSaving(false);
    setDialogOpen(false);
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Hapus destination ini?")) return;
    const ok = await deleteDestination(id);
    if (ok) {
      setData((prev) => prev.filter((d) => d.ID !== id));
    }
  };

  // Label auth yang tampil di tabel
  const getAuthLabel = (dest: WebhookDestination) => {
    if (dest.HeaderKey) return `Header: ${dest.HeaderKey}`;
    if (dest.Token) return "Bearer token";
    return "—";
  };

  const columns: ColumnDef<WebhookDestination>[] = [
    {
      accessorKey: "Name",
      header: "Nama",
    },
    {
      accessorKey: "URL",
      header: "URL",
      cell: ({ row }) => (
        <span className="font-mono text-xs break-all">{row.getValue("URL")}</span>
      ),
    },
    {
      id: "auth",
      header: "Autentikasi",
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground font-mono">
          {getAuthLabel(row.original)}
        </span>
      ),
    },
    {
      accessorKey: "Mode",
      header: "Mode",
      cell: ({ row }) => {
        const mode: string = row.getValue("Mode");
        return (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              mode === "sandbox"
                ? "bg-yellow-100 text-yellow-800"
                : "bg-green-100 text-green-800"
            }`}
          >
            {mode === "sandbox" ? "Sandbox" : "Production"}
          </span>
        );
      },
    },
    {
      accessorKey: "IsActive",
      header: "Status",
      cell: ({ row }) => {
        const active: boolean = row.getValue("IsActive");
        return (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              active ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-600"
            }`}
          >
            {active ? "Aktif" : "Nonaktif"}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Aksi",
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => openEdit(row.original)}
          >
            <Pencil className="w-3 h-3" />
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => handleDelete(row.original.ID)}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          {data.length} destination terdaftar
        </p>
        <Button onClick={openCreate} size="sm">
          <Plus className="w-4 h-4 mr-1" />
          Tambah Destination
        </Button>
      </div>

      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => (
                  <TableHead key={h.id}>
                    {h.isPlaceholder
                      ? null
                      : flexRender(h.column.columnDef.header, h.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Belum ada destination. Tambahkan yang pertama!
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editTarget ? "Edit Destination" : "Tambah Destination"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="dest-name">Nama</Label>
              <Input
                id="dest-name"
                placeholder="contoh: Baksos Laravel"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="dest-url">URL Webhook</Label>
              <Input
                id="dest-url"
                placeholder="https://app.example.com/webhook/midtrans"
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
              />
            </div>

            {/* Auth type selector */}
            <div className="space-y-1">
              <Label>Tipe Autentikasi</Label>
              <Select value={authMode} onValueChange={(v) => handleAuthModeChange(v as AuthMode)}>
                <SelectTrigger id="dest-auth-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Tanpa autentikasi</SelectItem>
                  <SelectItem value="bearer">Bearer token</SelectItem>
                  <SelectItem value="custom">Custom header (api_key, dll)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bearer token */}
            {authMode === "bearer" && (
              <div className="space-y-1">
                <Label htmlFor="dest-token">Bearer Token</Label>
                <Input
                  id="dest-token"
                  placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                  value={form.token}
                  onChange={(e) => setForm((f) => ({ ...f, token: e.target.value }))}
                />
              </div>
            )}

            {/* Custom header */}
            {authMode === "custom" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="dest-header-key">Nama Header</Label>
                  <Input
                    id="dest-header-key"
                    placeholder="api_key"
                    value={form.header_key}
                    onChange={(e) => setForm((f) => ({ ...f, header_key: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="dest-header-value">Nilai Header</Label>
                  <Input
                    id="dest-header-value"
                    placeholder="your-secret-key"
                    value={form.header_value}
                    onChange={(e) => setForm((f) => ({ ...f, header_value: e.target.value }))}
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Mode</Label>
                <Select
                  value={form.mode}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, mode: v as DestinationMode }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="production">Production</SelectItem>
                    <SelectItem value="sandbox">Sandbox</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label>Status</Label>
                <Select
                  value={form.is_active ? "active" : "inactive"}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, is_active: v === "active" }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktif</SelectItem>
                    <SelectItem value="inactive">Nonaktif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Batal
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
